<template>
  <div class="preika-insight-page">
    <section class="preika-page-hero preika-page-hero--leave">
      <div>
        <p class="preika-page-hero__eyebrow">PAID LEAVE PLANNER</p>
        <h1>有休取得計画</h1>
        <p>現在残数、付与後の取得状況、次回付与、今後の申請予定を一つの画面で確認します。</p>
      </div>
      <div class="preika-page-controls">
        <label><span>基準日</span><input v-model="asOf" type="date" @change="fetchData"></label>
        <label><span>予定表示</span>
          <select v-model.number="horizonMonths" @change="fetchData">
            <option :value="3">3か月</option>
            <option :value="6">6か月</option>
            <option :value="12">12か月</option>
          </select>
        </label>
        <button type="button" class="preika-btn preika-btn--primary" :disabled="loading" @click="fetchData"><CIcon name="cil-loop"/>再集計</button>
      </div>
    </section>

    <div v-if="loading" class="preika-analytics-loading"><span class="preika-analytics-spinner"></span>有休情報を集計中...</div>
    <div v-else-if="error" class="preika-analytics-error" role="alert">
      <div><strong>有休取得計画を取得できませんでした</strong><small>{{ error }}</small></div>
      <button type="button" @click="fetchData">再取得</button>
    </div>

    <template v-else>
      <section class="preika-analytics-kpis">
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-sun"/></span>
          <small>{{ managementView ? '有休残数（全体）' : '現在の有休残数' }}</small>
          <strong>{{ formatDays(totalBalance) }}</strong>
          <p>{{ people.length }}名を集計</p>
        </article>
        <article class="preika-analytics-kpi" :class="{ 'preika-analytics-kpi--warning': attentionCount > 0 }">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-warning"/></span>
          <small>取得計画の要確認</small>
          <strong>{{ attentionCount }}</strong>
          <p>目標未達・設定不足</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-calendar-check"/></span>
          <small>今後の有休予定</small>
          <strong>{{ schedule.length }}</strong>
          <p>承認済み・申請中を含む</p>
        </article>
      </section>

      <section v-if="!managementView && people.length" class="preika-leave-spotlight">
        <div class="preika-progress-ring preika-progress-ring--large" :style="progressStyle(people[0])">
          <div><strong>{{ Math.round(Number(people[0].progressRate || 0)) }}%</strong><small>5日目標</small></div>
        </div>
        <div class="preika-leave-spotlight__copy">
          <span class="preika-tone-badge" :class="'preika-tone-badge--' + people[0].tone">{{ personToneLabel(people[0]) }}</span>
          <h2>{{ people[0].userName }}さんの取得プラン</h2>
          <p v-if="people[0].configurationMissing">入社日・付与月・所定労働日数・付与ルールのいずれかが設定されていません。社内の管理者に確認してください。</p>
          <p v-else-if="!people[0].obligationApplies">現在の付与ルールは10日未満のため、5日取得目標の対象外として表示しています。</p>
          <p v-else-if="Number(people[0].targetRemainingDays) <= 0">今期の5日取得目標を達成済みです。</p>
          <p v-else>次回付与までに、あと<strong>{{ formatDays(people[0].targetRemainingDays) }}</strong>の取得を計画してください。</p>
        </div>
        <div class="preika-leave-spotlight__metrics">
          <div><small>現在残数</small><strong>{{ formatDays(people[0].balanceDays) }}</strong></div>
          <div><small>付与後取得</small><strong>{{ formatDays(people[0].usedSinceGrant) }}</strong></div>
          <div><small>次回付与</small><strong>{{ formatDate(people[0].nextGrantDate) }}</strong><span>{{ formatDays(people[0].nextGrantDays) }}見込</span></div>
        </div>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>LEAVE STATUS</p><h2>{{ managementView ? 'スタッフ別の取得状況' : '取得状況の詳細' }}</h2></div>
          <router-link to="/paidandsubstitutes/list" class="preika-text-link">休暇申請を開く<CIcon name="cil-arrow-right"/></router-link>
        </header>

        <div v-if="people.length" class="preika-table-scroll">
          <table class="preika-data-table preika-leave-table">
            <thead>
              <tr>
                <th>スタッフ</th>
                <th>現在残数</th>
                <th>付与後取得</th>
                <th>5日目標</th>
                <!--
                  「次回付与」は入社日と付与表から計算した見込み。
                  **本当に付いたかどうかは記録を見ないと分からない。**
                  付与が動いていない人（入社日が空・付与月が0・処理が
                  止まっていた）は、見込みだけが出て記録が空になる。
                -->
                <th>直近の付与</th>
                <th>次回付与</th>
                <th>今後の予定</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="person in people" :key="person.userId">
                <th scope="row">
                  <span class="preika-matrix-person">
                    <span class="preika-matrix-person__avatar">{{ initial(person.userName) }}</span>
                    <span><strong>{{ person.userName }}</strong><small>{{ person.departmentName || '未所属' }}</small></span>
                  </span>
                </th>
                <td><strong>{{ formatDays(person.balanceDays) }}</strong></td>
                <td>{{ formatDays(person.usedSinceGrant) }}</td>
                <td>
                  <div v-if="person.obligationApplies" class="preika-mini-progress">
                    <span><i :style="{ width: progressWidth(person) }"></i></span>
                    <small>残り {{ formatDays(person.targetRemainingDays) }}</small>
                  </div>
                  <span v-else class="preika-muted-text">対象外</span>
                </td>
                <td>
                  <template v-if="person.grantedYearMonth">
                    <strong>{{ formatDays(person.grantedDays) }}</strong>
                    <small class="preika-table-subtext">
                      {{ grantedMonthLabel(person.grantedYearMonth) }}分 / {{ formatDate(person.grantedAt) }}
                    </small>
                  </template>
                  <span v-else class="preika-muted-text">記録なし</span>
                </td>
                <td><strong>{{ formatDate(person.nextGrantDate) }}</strong><small class="preika-table-subtext">{{ formatDays(person.nextGrantDays) }}見込</small></td>
                <td><strong>{{ person.upcomingApprovedCount || 0 }}</strong> 承認済み / <strong>{{ person.upcomingPendingCount || 0 }}</strong> 申請中</td>
                <td><span class="preika-tone-badge" :class="'preika-tone-badge--' + person.tone">{{ personToneLabel(person) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="preika-analytics-empty"><CIcon name="cil-sun"/><strong>有休情報を表示できるスタッフがいません</strong><p>ユーザ設定と有休付与ルールを確認してください。</p></div>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>UPCOMING SCHEDULE</p><h2>今後の有休予定</h2></div>
          <span class="preika-neutral-badge">{{ formatDate(data.asOfDate) }}〜{{ formatDate(data.horizonEndDate) }}</span>
        </header>
        <div v-if="schedule.length" class="preika-leave-schedule">
          <router-link v-for="item in schedule" :key="item.id" :to="item.route || '/paidandsubstitutes/list'" class="preika-leave-schedule__item">
            <span class="preika-leave-schedule__date"><strong>{{ shortDate(item.startDate) }}</strong><small>{{ scheduleRange(item) }}</small></span>
            <span class="preika-leave-schedule__person"><strong>{{ item.userName }}</strong><small>{{ formatDays(item.estimatedDays) }}</small></span>
            <span class="preika-status-badge" :class="'preika-status-badge--' + statusTone(item.status)">{{ item.statusName }}</span>
            <CIcon name="cil-chevron-right"/>
          </router-link>
        </div>
        <div v-else class="preika-analytics-empty preika-analytics-empty--compact">表示期間内の有休予定はありません。</div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { formatWeekday } from '../utils/displayDate'
import { clampManagementPercent, formatManagementDays } from '../utils/managementInsights'
import { safeErrorDigest } from '../utils/safeResponseMessage'

export default {
  name: 'PaidLeavePlanner',

  data () {
    return {
      asOf: moment().format('YYYY-MM-DD'),
      horizonMonths: 6,
      loading: false,
      error: '',
      data: { scope: 'SELF', asOfDate: '', horizonEndDate: '', managementView: false, people: [], schedule: [] }
    }
  },

  computed: {
    people () { return Array.isArray(this.data.people) ? this.data.people : [] },
    schedule () { return Array.isArray(this.data.schedule) ? this.data.schedule : [] },
    managementView () { return this.data.managementView === true },
    totalBalance () { return this.people.reduce((sum, person) => sum + Number(person.balanceDays || 0), 0) },
    attentionCount () {
      return this.people.filter(person => person.configurationMissing || ['warning', 'danger'].includes(person.tone)).length
    }
  },

  mounted () { this.fetchData() },

  methods: {
    /** 202610 を「2026年10月」に。 */
    grantedMonthLabel (yearMonth) {
      const text = String(yearMonth || '')
      if (text.length !== 6) return text
      return `${text.slice(0, 4)}年${Number(text.slice(4, 6))}月`
    },
    formatDays: formatManagementDays,

    fetchData () {
      this.loading = true
      this.error = ''
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/paid-leave-planner', {
          params: { asOf: this.asOf, months: this.horizonMonths }
        })
        .then(response => {
          const value = response.data || {}
          this.data = {
            scope: value.scope || 'SELF',
            asOfDate: value.asOfDate || this.asOf,
            horizonEndDate: value.horizonEndDate || '',
            managementView: value.managementView === true,
            people: Array.isArray(value.people) ? value.people : [],
            schedule: Array.isArray(value.schedule) ? value.schedule : []
          }
        })
        .catch(error => {
          this.error = '通信または有休集計処理でエラーが発生しました。'
          console.error('有休取得計画取得失敗:', safeErrorDigest(error))
        })
        .finally(() => { this.loading = false })
    },

    initial (value) { return String(value || '人').substring(0, 1) },
    formatDate (value) { return value ? moment(value, 'YYYY-MM-DD').format('YYYY/MM/DD') : '未設定' },
    shortDate (value) { return value ? moment(value, 'YYYY-MM-DD').format('M/D') : '—' },
    progressWidth (person) { return `${clampManagementPercent(person.progressRate)}%` },
    progressStyle (person) { return { '--preika-ring-progress': `${clampManagementPercent(person.progressRate)}%` } },

    personToneLabel (person) {
      if (person.configurationMissing) return '設定確認'
      if (!person.obligationApplies) return '目標対象外'
      if (Number(person.targetRemainingDays || 0) <= 0) return '達成済み'
      if (person.tone === 'danger') return '期限接近'
      if (person.tone === 'warning') return '計画推奨'
      return '計画中'
    },

    statusTone (status) {
      return ({ '1': 'neutral', '2': 'pending', '3': 'rejected', '4': 'approved' })[String(status)] || 'neutral'
    },

    scheduleRange (item) {
      // 1日だけの予定は曜日を出す。moment の既定は英語（Fri）なので、書き方は utils/displayDate.js で
      // 1か所に決め、そこで ja を付ける（アプリ全体の言語は変えない）。
      if (!item.endDate || item.endDate === item.startDate) return formatWeekday(item.startDate)
      return `${this.shortDate(item.startDate)}〜${this.shortDate(item.endDate)}`
    }
  }
}
</script>
